import { ToasterMode } from '@creditsights/styleguide-web';
import { useMutation, UseMutationResult, useQueryClient } from 'react-query';
import { DocketCaseDocument } from '../../models/CompanyDockets';
import {
  DOWNLOAD_DOCUMENT_TAKING_LONGER_WARNING_MESSAGE,
  ERROR_DOWNLOAD_DOCUMENT_MESSAGE,
  RESTRICTED_DOCUMENT_ERROR_MESSAGE,
} from '../../pages/Company/CompanyDocketsTab/CompanyDocketsTab.constants';
import { ApiError } from '../../services/api-error.model';
import { downloadDocketItem, getDownloadJobStatus } from '../../services/dockets/dockets.service';
import { DownloadItemResponse } from '../../services/dtos/companyDockets/dockets.dto';
import { Channels } from '../../utils/channels';
import { sleep } from '../../utils/helperUtils';
import { queryKeys } from '../queryKeyFactory';
import { useToast } from '../toast/useToast';
import { CaseDocumentsQueryFilters } from './useFetchCaseDocuments';

export interface UseCheckDownloadDocketDocumentStatusPayload {
  requestId: string;
  docketCaseDocument: DocketCaseDocument;
  page: number;
  pageSize: number;
  filters: CaseDocumentsQueryFilters;
  onSuccess: (response: DownloadItemResponse) => void;
  onSettled: () => void;
}

const RETRY_COUNT_TO_SHOW_WARNING = 10;
const RETRY_COUNT = 25;
const RETRY_DELAY = 2000; // ms

const validateResponse = ({ requestStatus, requestResultMessage }: DownloadItemResponse) => {
  if (requestStatus.toUpperCase() === 'FAILED') {
    const error = new ApiError(requestResultMessage);
    error.response = { status: 403 };
    throw error;
  }
  if (requestStatus.toUpperCase() !== 'SUCCESS') {
    throw new ApiError('Docket document download is still processing');
  }
};

const isRestrictedError = (error: ApiError): boolean => Number(error?.response?.status) === 403;

export default (): UseMutationResult<
  DownloadItemResponse,
  ApiError,
  UseCheckDownloadDocketDocumentStatusPayload,
  void
> => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation(
    async ({ requestId, docketCaseDocument }) => {
      try {
        const response = await getDownloadJobStatus(requestId);
        validateResponse(response);
        return response;
      } catch (e) {
        if ((e as ApiError).response?.status === 500) {
          const response = await downloadDocketItem(
            docketCaseDocument.docketItemCaseId,
            docketCaseDocument.docketItemId
          );
          validateResponse(response);
          return response;
        }
        throw e;
      }
    },
    {
      onSuccess: async (response, { docketCaseDocument, page, pageSize, onSuccess }) => {
        // Once either /download or /download-status/ api resolves with a permalink we invalidate the cache to re-fetch case dockets
        // on the first re-fetch, permalink is null and file available is still false
        // therefore, we sleep for a second and only then invalidate the cache
        await sleep(1000);
        onSuccess(response);
        // invalidating both filtered and unfiltered document caches
        await queryClient.invalidateQueries(
          queryKeys.dockets.fetchDocuments.queryForInvalidation(docketCaseDocument.docketItemCaseId, page, pageSize)
        );
        await queryClient.invalidateQueries(
          queryKeys.dockets.fetchDocuments.attachmentsForInvalidation(
            docketCaseDocument.docketItemCaseId,
            docketCaseDocument.docketNumber,
            docketCaseDocument.filedDate,
            pageSize
          )
        );
        await Channels.docketDocumentsChannel.postMessage(null);
        return response;
      },
      onError: (error) => {
        showToast(
          isRestrictedError(error) ? RESTRICTED_DOCUMENT_ERROR_MESSAGE : ERROR_DOWNLOAD_DOCUMENT_MESSAGE,
          ToasterMode.ERROR
        );
      },
      onSettled: (data, error, { onSettled }) => {
        onSettled();
      },
      retry: (failureCount: number, error) => {
        if (isRestrictedError(error)) {
          return false;
        }
        if (failureCount + 1 === RETRY_COUNT_TO_SHOW_WARNING) {
          showToast(DOWNLOAD_DOCUMENT_TAKING_LONGER_WARNING_MESSAGE, ToasterMode.WARNING);
        }
        return failureCount + 1 !== RETRY_COUNT;
      },
      retryDelay: RETRY_DELAY,
    }
  );
};
